import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  ErrorMessageFixedPositions,
  ErrorMessageSchemeDefinition,
  ErrorMessageSchemeName
} from "./constants/error-message-types";
import {
  ERROR_MESSAGE_FIXED_POSITION_ALIGNMENT_TOP,
  ERROR_MESSAGE_SCHEME_CONFIG,
  ERROR_MESSAGE_SCHEME_MAP
} from "./constants/error-message-constants";
import { ErrorMessageSchemeConfiguration } from "../../constants/colors";
import pfx from "../../utils/style-class-prefix-helper";
import { SvgType } from "../../common/svg-type";
import { SvgLoader } from "../../common/svg-loader";

import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot [unnamed] - holds any number of HTML elements.
 * @slot headline - holds a eon-ui-headline component.
 */
@Component({
  tag: "eon-ui-error-message",
  styleUrl: "error-message.scss",
  shadow: true
})
export class ErrorMessage implements ColorableComponent<ErrorMessageSchemeConfiguration<ErrorMessageSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: ErrorMessageSchemeConfiguration<ErrorMessageSchemeDefinition> = ERROR_MESSAGE_SCHEME_CONFIG;
  schemeMap = ERROR_MESSAGE_SCHEME_MAP;
  private readonly loader: SvgLoader;

  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: ErrorMessageSchemeDefinition;

  /**
   * Raised when the message box has been closed via X icon.
   */
  @Event() messageClose: EventEmitter;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiErrorMessageElement;
  componentWrapper!: HTMLElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: ErrorMessageSchemeName;

  /**
   * Change to show error message
   */
  @Prop({ reflect: true, mutable: true }) isVisible: boolean;

  /**
   * Icon name
   */
  @Prop({ reflect: true, mutable: true }) iconName: string;

  /**
   * Represents the internal color of the component.
   * It will be read by colorable-component.ts getColorOfNearestBackground() function to realize
   * the detection of backgrounds inside of components, hidden by shadow dom.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true, mutable: true }) innerBackground: string;

  /**
   * Close error message automatically after the defined number of seconds
   */
  @Prop({ reflect: true }) closeTimer: number;

  /**
   * Select to position error message on the top/bottom of the page
   */
  @Prop({ reflect: true }) fixedPosition: boolean;

  /**
   * Define the alignment of the fixed positioned error message
   */
  @Prop() fixedPositionAlignment: ErrorMessageFixedPositions = ERROR_MESSAGE_FIXED_POSITION_ALIGNMENT_TOP;

  /**
   * if this property is true, an optional closing-X will be shown
   */
  @Prop() showClosingX: boolean = true;

  /**
   * Show or hide the left border
   */
  @Prop() showLeftBorder: boolean = true;
  /**
   * Variable to store the automatic color value from the headline map
   */
  @State() headlineScheme: any;

  /**
   * States to control adding/removing animation classes
   */
  @State() visibleClass: boolean = false;
  @State() hidingClass: boolean = false;

  /**
   * Variable to reset the animation timeout
   * @private
   */
  private animationTimeout: any;

  constructor() {
    this.loader = new SvgLoader();
  }

  componentWillLoad() {
    this.applyScheme();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.handleVisibility();
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
    this.innerBackground = this.activeScheme.backgroundColor;
    this.assignIcon();
  }

  @Watch("isVisible")
  handleVisibility() {
    if (this.animationTimeout) {
      window.clearTimeout(this.animationTimeout);
      this.visibleClass = false;
      this.hidingClass = false;
      this.animationTimeout = false;
    }

    if (this.isVisible) {
      this.visibleClass = true;
      this.componentWrapper.focus();

      if (this.closeTimer) {
        window.setTimeout(() => {
          this.isVisible = false;
        }, this.closeTimer * 1000);
      }
    } else {
      this.hidingClass = true;
      this.componentWrapper.classList.add(pfx("hiding").toString());
      this.animationTimeout = window.setTimeout(() => {
        this.visibleClass = false;
        this.hidingClass = false;
      }, 500);
    }
  }

  @Watch("iconName")
  assignIcon() {
    const iconsMap = {
      error: "error",
      warning: "alert",
      success: "value_proposition",
      info: "info",
      white: "info",
      grey100: "info"
    };

    const iconIsPredefined = Object.keys(iconsMap).indexOf(this.scheme) > -1;
    const isCustomIcon =
      this.iconName &&
      Object.entries(iconsMap).filter(([key, value]) => key === this.scheme && value === this.iconName)?.length === 0;

    if (!isCustomIcon) {
      if (iconIsPredefined) {
        this.setIconIfExists(iconsMap[this.scheme]);
      } else {
        this.iconName = "";
      }
    }
  }

  private closeMessage(event) {
    if (event.type === "keydown") {
      if (event.keyCode === 13 || event.keyCode === 32) {
        this.isVisible = false;
      }
    } else {
      this.isVisible = false;
    }
    this.messageClose.emit();
  }

  /**
   * Check if icon does exist. If not, reset IconName.
   */
  setIconIfExists(icon: string) {
    this.loader
      .requestSvg(icon, SvgType.ICON)
      .then(() => {
        this.iconName = icon;
      })
      .catch(() => {
        this.iconName = "";
      });
  }

  render() {
    return (
      <Host>
        <div
          class={pfx({
            "error-message": true,
            fixed: this.fixedPosition,
            [`${this.fixedPositionAlignment}`]: !!this.fixedPosition,
            visible: this.visibleClass,
            hiding: this.hidingClass,
            ["hide-closing-x"]: !this.showClosingX,
            ["hide-border"]: !this.showLeftBorder
          })}
          ref={(el) => (this.componentWrapper = el as HTMLElement)}
        >
          {this.showClosingX && (
            <div class={pfx("error-message-close")}>
              <eon-ui-icon
                class={pfx("icon-closing-x")}
                onClick={(event) => this.closeMessage(event)}
                onKeyDown={(event) => this.closeMessage(event)}
                name="close"
                aria-label="close"
                role="button"
                tabIndex={0}
              ></eon-ui-icon>
            </div>
          )}
          <div class={pfx("error-message-content")}>
            <div class={pfx("error-message-headline-wrapper")}>
              {this.iconName && (
                <div class={pfx("error-message-icon")}>
                  <eon-ui-icon name={this.iconName} scheme={this.headlineScheme}></eon-ui-icon>
                </div>
              )}
              <div class={pfx("error-message-headline")}>
                <slot name="headline"></slot>
              </div>
            </div>
            <div class={pfx("error-message-text")}>
              <slot />
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
